<template>
  <div class="bulk-page">
    <!-- Top bar -->
    <div
      class="topbar d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3"
    >
      <div class="min-w-0">
        <h4 class="fw-bold mb-1">Monthly Bulk Contributions</h4>
        <div class="text-muted small">
          Enter amounts for a period. Existing records will be topped up and
          extra will carry forward as allocations.
        </div>
      </div>

      <div class="d-flex flex-wrap gap-2 align-items-end">
        <div>
          <label class="form-label small text-muted mb-1">
            Financial Year
          </label>
          <select
            v-model="financialYearRuleId"
            class="form-select form-select-sm"
            :disabled="loading"
            @change="onFinancialYearChanged"
          >
            <option
              v-for="fy in financialYears"
              :key="fy.id"
              :value="fy.id"
            >
              {{ fy.year_key }} {{ fy.is_active ? "(active)" : "" }}
            </option>
          </select>
        </div>

        <div>
          <label class="form-label small text-muted mb-1">Period</label>
          <input
            v-model="period"
            type="month"
            class="form-control form-control-sm"
            :min="selectedYearStartPeriod"
            :max="selectedYearEndPeriod"
            :disabled="loading"
          />
        </div>

        <button
          class="btn btn-outline-secondary btn-sm"
          @click="preview"
          :disabled="loading || !period || !financialYearRuleId"
        >
          <i class="bi bi-arrow-repeat me-1"></i
          >{{ loading ? "Refreshing..." : "Refresh" }}
        </button>

        <button
          class="btn btn-primary btn-sm"
          @click="openConfirm"
          :disabled="
            saving ||
            !isViewingActiveYear ||
            submitItems.length === 0 ||
            blockingIssues.length > 0
          "
        >
          <i class="bi bi-upload me-1"></i
          >{{ saving ? "Processing..." : "Submit" }}
          <span v-if="submitItems.length" class="badge text-bg-light ms-2">{{
            submitItems.length
          }}</span>
        </button>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger py-2">
      {{ error }}
    </div>

    <div
      v-if="selectedFinancialYear"
      class="card border-0 shadow-sm mb-3"
    >
      <div class="card-body py-3">
        <div
          class="d-flex flex-wrap justify-content-between align-items-center gap-2"
        >
          <div>
            <div class="d-flex align-items-center flex-wrap gap-2">
              <strong>{{ selectedFinancialYear.year_key }}</strong>
              <span
                class="badge rounded-pill"
                :class="
                  isViewingActiveYear
                    ? 'text-bg-success'
                    : 'text-bg-secondary'
                "
              >
                {{ isViewingActiveYear ? "Active year" : "Past year" }}
              </span>
            </div>

            <div class="text-muted small mt-1">
              {{ niceDate(selectedFinancialYear.start_date) }}
              –
              {{ niceDate(selectedFinancialYear.end_date) }}
            </div>
          </div>

          <button
            v-if="!isViewingActiveYear"
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="goToActiveYear"
          >
            <i class="bi bi-calendar-check me-1"></i>
            Return to active year
          </button>
        </div>

        <div
          v-if="!isViewingActiveYear"
          class="alert alert-light border mt-3 mb-0 py-2"
        >
          <i class="bi bi-info-circle me-1"></i>
          This financial year is closed. Bulk contribution processing is
          disabled, but you can still review the preview.
        </div>
      </div>
    </div>

    <!-- Controls -->
    <div class="card border-0 shadow-sm mb-3">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-md-3">
            <label class="form-label small text-muted mb-1"
              >Default expected date</label
            >
            <input
              v-model="defaults.expected_date"
              type="date"
              class="form-control form-control-sm"
              :min="selectedFinancialYear?.start_date || undefined"
              :max="selectedFinancialYear?.end_date || undefined"
              :disabled="!isViewingActiveYear"
            />
          </div>

          <div class="col-md-3">
            <label class="form-label small text-muted mb-1"
              >Default paid date</label
            >
            <input
              v-model="defaults.paid_date"
              type="date"
              class="form-control form-control-sm"
              :min="selectedFinancialYear?.start_date || undefined"
              :max="selectedFinancialYear?.end_date || undefined"
              :disabled="!isViewingActiveYear"
            />
          </div>

          <div class="col-md-4">
            <label class="form-label small text-muted mb-1">Search</label>
            <div class="input-group input-group-sm">
              <span class="input-group-text"><i class="bi bi-search"></i></span>
              <input
                v-model="q"
                class="form-control"
                placeholder="Name / phone / email"
              />
              <button
                v-if="q"
                class="btn btn-outline-secondary"
                @click="q = ''"
                title="Clear search"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>

          <div class="col-md-2 d-flex gap-2">
            <button
              class="btn btn-outline-dark btn-sm w-100"
              @click="fillCommitted"
              :disabled="rows.length === 0 || !isViewingActiveYear"
            >
              Fill committed
            </button>
            <button
              class="btn btn-outline-secondary btn-sm"
              @click="clearInputs"
              :disabled="rows.length === 0 || !isViewingActiveYear"
              title="Clear inputs"
            >
              <i class="bi bi-eraser"></i>
            </button>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2 mt-3">
          <span class="badge text-bg-light border"
            >Ready: <strong class="ms-1">{{ submitItems.length }}</strong></span
          >
          <span class="badge text-bg-light border"
            >Existing: <strong class="ms-1">{{ existingCount }}</strong></span
          >
          <span class="badge text-bg-light border"
            >No opening:
            <strong class="ms-1">{{ missingOpeningCount }}</strong></span
          >
          <span class="badge text-bg-light border"
            >No commitment:
            <strong class="ms-1">{{ missingCommitmentCount }}</strong></span
          >

          <span v-if="blockingIssues.length" class="badge text-bg-danger">
            {{ blockingIssues[0] }}
          </span>
        </div>

        <div class="small text-muted mt-2">
          Tip: If a member needs an exception (custom expected date), record it
          from the Contributions page (single record), not here.
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="card border-0 shadow-sm">
      <div
        class="card-header bg-white d-flex justify-content-between align-items-center"
      >
        <div class="fw-semibold">Preview</div>
        <div class="text-muted small">
          Showing <strong>{{ filteredRows.length }}</strong> members
        </div>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width: 42%">Member</th>
                <th style="width: 14%">Opening</th>
                <th style="width: 14%">Committed</th>
                <th style="width: 16%">Existing</th>
                <th style="width: 14%">Amount</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="r in filteredRows"
                :key="r.user.id"
                :class="rowClass(r)"
              >
                <!-- Member -->
                <td class="min-w-0">
                  <div class="fw-semibold text-truncate">{{ r.user.name }}</div>
                  <div class="text-muted small text-truncate">
                    {{ r.user.phone || r.user.email || "-" }}
                  </div>

                  <!-- Actions -->
                  <div class="d-flex flex-wrap gap-2 mt-2">
                    <button
                      v-if="!hasOpening(r)"
                      class="btn btn-outline-warning btn-sm"
                      type="button"
                      :disabled="!isViewingActiveYear"
                      @click="openOpeningForRow(r)"
                    >
                      <i class="bi bi-bank me-1"></i>Set opening balance
                    </button>

                    <button
                      v-else-if="r.commitment_amount == null"
                      class="btn btn-outline-primary btn-sm"
                      type="button"
                      :disabled="!isViewingActiveYear"
                      @click="openCommitmentForRow(r)"
                    >
                      <i class="bi bi-plus-lg me-1"></i>Set commitment
                    </button>
                  </div>

                  <!-- Warnings/errors -->
                  <div v-if="r.ui.error" class="small text-danger mt-1">
                    <i class="bi bi-x-circle me-1"></i>{{ r.ui.error }}
                  </div>
                  <div v-else-if="r.ui.warning" class="small text-warning mt-1">
                    <i class="bi bi-exclamation-triangle me-1"></i
                    >{{ r.ui.warning }}
                  </div>
                </td>

                <!-- Opening -->
                <td>
                  <div v-if="hasOpening(r)" class="small">
                    <span class="badge text-bg-light border">
                      {{ fmt(openingAmount(r)) }} RWF
                    </span>
                    <div v-if="openingAsOf(r)" class="text-muted mt-1">
                      As of:
                      <span class="badge text-bg-light border">{{
                        openingAsOf(r)
                      }}</span>
                    </div>
                  </div>
                  <span v-else class="badge text-bg-warning">Missing</span>
                </td>

                <!-- Committed -->
                <td>
                  <span
                    v-if="r.commitment_amount != null"
                    class="badge text-bg-light border"
                  >
                    {{ fmt(r.commitment_amount) }} RWF
                  </span>
                  <div v-else class="text-muted small">—</div>
                </td>

                <!-- Existing -->
                <td>
                  <div v-if="r.existing" class="small">
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <span
                        class="badge"
                        :class="statusBadge(r.existing.status)"
                      >
                        {{ r.existing.status }}
                      </span>
                      <span class="text-muted"
                        >{{ fmt(r.existing.amount) }} RWF</span
                      >
                    </div>
                    <div v-if="r.existing.paid_date" class="text-muted mt-1">
                      Paid: {{ r.existing.paid_date }}
                    </div>
                  </div>
                  <span v-else class="text-muted small">—</span>
                </td>

                <!-- Amount -->
                <td>
                  <input
                    v-model.number="r.input.amount"
                    type="number"
                    min="0"
                    class="form-control form-control-sm"
                    :disabled="!isViewingActiveYear || isRowBlocked(r)"
                    @input="validateRow(r)"
                    placeholder="0"
                  />
                  <div v-if="isRowBlocked(r)" class="small text-muted mt-1">
                    {{ blockReason(r) }}
                  </div>
                </td>
              </tr>

              <tr v-if="loading">
                <td colspan="5" class="text-center text-muted py-4">
                  Loading preview...
                </td>
              </tr>

              <tr v-if="!loading && filteredRows.length === 0">
                <td colspan="5" class="text-center text-muted py-4">
                  No members found
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        class="card-footer bg-white d-flex justify-content-between align-items-center"
      >
        <div class="text-muted small">
          Ready to submit: <strong>{{ submitItems.length }}</strong>
          <span v-if="blockingIssues.length" class="ms-2 text-danger"
            >• {{ blockingIssues[0] }}</span
          >
        </div>

        <button
          class="btn btn-primary btn-sm"
          @click="openConfirm"
          :disabled="
            saving ||
            !isViewingActiveYear ||
            submitItems.length === 0 ||
            blockingIssues.length > 0
          "
        >
          <i class="bi bi-upload me-1"></i
          >{{ saving ? "Processing..." : "Submit" }}
        </button>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div class="modal fade" id="bulkConfirmModal" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Submission</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>

          <div class="modal-body">
            <div class="alert alert-info py-2">
              <div class="d-flex flex-wrap gap-3">
                <div><strong>Period:</strong> {{ period }}</div>
                <div><strong>Rows:</strong> {{ submitItems.length }}</div>
              </div>
              <div class="small text-muted mt-2">
                Default Paid: <strong>{{ defaults.paid_date || "—" }}</strong> •
                Default Expected:
                <strong>{{ defaults.expected_date || "—" }}</strong>
              </div>
            </div>

            <div v-if="blockingIssues.length" class="alert alert-danger py-2">
              <div class="fw-semibold mb-1">Fix these first:</div>
              <ul class="mb-0">
                <li v-for="(b, idx) in blockingIssues" :key="idx">{{ b }}</li>
              </ul>
            </div>

            <div class="small text-muted mt-3">Sample (first 8 rows)</div>
            <div class="table-responsive">
              <table class="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th class="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(it, idx) in submitItems.slice(0, 8)" :key="idx">
                    <td>{{ memberName(it.user_id) }}</td>
                    <td class="text-end">{{ fmt(it.amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-outline-secondary" data-bs-dismiss="modal">
              Cancel
            </button>
            <button
              class="btn btn-primary"
              :disabled="
                saving ||
                !isViewingActiveYear ||
                blockingIssues.length > 0
              "
              @click="submit"
            >
              {{ saving ? "Processing..." : "Confirm & Submit" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <CommitmentModal
      ref="commitmentModal"
      :members="members"
      @saved="onAnyModalSaved"
    />
    <OpeningBalanceModal
      ref="openingModal"
      :members="members"
      @saved="onAnyModalSaved"
    />
  </div>
</template>

<script>
import http from "@/services/http";
import { Modal } from "bootstrap";
import CommitmentModal from "@/components/CommitmentModal.vue";
import OpeningBalanceModal from "@/components/OpeningBalanceModal.vue";

export default {
  name: "AdminBulkContributions",
  components: { CommitmentModal, OpeningBalanceModal },

  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");

    return {
      period: `${yyyy}-${mm}`,
      loading: false,
      saving: false,
      error: "",

      q: "",
      rows: [],
      members: [],
      financialYears: [],
      financialYearRuleId: "",
      activeFinancialYearId: null,

      defaults: {
        expected_date: "",
        paid_date: `${yyyy}-${mm}-${dd}`,
      },

      confirmModal: null,
      result: null,
    };
  },

  computed: {
    selectedFinancialYear() {
      return (
        this.financialYears.find(
          (fy) =>
            String(fy.id) === String(this.financialYearRuleId)
        ) || null
      );
    },

    activeFinancialYear() {
      return (
        this.financialYears.find((fy) => Boolean(fy.is_active)) || null
      );
    },

    isViewingActiveYear() {
      return Boolean(this.selectedFinancialYear?.is_active);
    },

    selectedYearStartPeriod() {
      return this.selectedFinancialYear?.start_date
        ? String(this.selectedFinancialYear.start_date).slice(0, 7)
        : "";
    },

    selectedYearEndPeriod() {
      return this.selectedFinancialYear?.end_date
        ? String(this.selectedFinancialYear.end_date).slice(0, 7)
        : "";
    },

    selectedYearDefaultPeriod() {
      const today = this.monthKeyToday();

      if (!this.selectedYearStartPeriod || !this.selectedYearEndPeriod) {
        return today;
      }

      if (today < this.selectedYearStartPeriod) {
        return this.selectedYearStartPeriod;
      }

      if (today > this.selectedYearEndPeriod) {
        return this.selectedYearEndPeriod;
      }

      return today;
    },

    selectedYearDefaultDate() {
      const today = this.dateToday();
      const start = this.selectedFinancialYear?.start_date
        ? String(this.selectedFinancialYear.start_date).slice(0, 10)
        : "";
      const end = this.selectedFinancialYear?.end_date
        ? String(this.selectedFinancialYear.end_date).slice(0, 10)
        : "";

      if (!start || !end) return today;
      if (today < start) return start;
      if (today > end) return end;

      return today;
    },

    filteredRows() {
      const q = (this.q || "").trim().toLowerCase();
      if (!q) return this.rows;

      return (this.rows || []).filter((r) => {
        const u = r.user || {};
        const hay = `${u.name || ""} ${u.phone || ""} ${
          u.email || ""
        }`.toLowerCase();
        return hay.includes(q);
      });
    },

    submitItems() {
      // only submit rows with amount > 0 AND not blocked
      return (this.rows || [])
        .filter((r) => !this.isRowBlocked(r) && Number(r.input.amount || 0) > 0)
        .map((r) => ({
          user_id: r.user.id,
          amount: Number(r.input.amount || 0),
          missed: false, // bulk no missed/override now
        }));
    },

    existingCount() {
      return (this.rows || []).filter((r) => !!r.existing).length;
    },

    missingOpeningCount() {
      return (this.rows || []).filter((r) => !this.hasOpening(r)).length;
    },

    missingCommitmentCount() {
      return (this.rows || []).filter(
        (r) => this.hasOpening(r) && r.commitment_amount == null,
      ).length;
    },

    blockingIssues() {
      const issues = [];

      if (!this.financialYearRuleId) {
        issues.push("Select a financial year.");
      }

      if (!this.isViewingActiveYear) {
        issues.push("Bulk processing is allowed only in the active financial year.");
      }

      if (!this.period) issues.push("Select a period.");

      const anyRowErrors = (this.rows || []).some((r) => !!r.ui.error);
      if (anyRowErrors) issues.push("Fix rows highlighted with errors.");

      const anyPaid = this.submitItems.length > 0;
      if (anyPaid && !this.defaults.paid_date)
        issues.push("Default Paid Date is required.");

      return issues;
    },
  },

  methods: {
    monthKeyToday() {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");

      return `${yyyy}-${mm}`;
    },

    dateToday() {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");

      return `${yyyy}-${mm}-${dd}`;
    },

    niceDate(value) {
      if (!value) return "-";

      const raw = String(value).slice(0, 10);
      const date = new Date(`${raw}T00:00:00`);

      if (Number.isNaN(date.getTime())) {
        return raw;
      }

      return date.toLocaleDateString();
    },

    fmt(n) {
      return Number(n || 0).toLocaleString();
    },

    statusBadge(status) {
      return (
        {
          paid: "text-bg-success",
          late: "text-bg-warning",
          missed: "text-bg-danger",
          partial: "text-bg-info",
        }[status] || "text-bg-secondary"
      );
    },

    memberName(userId) {
      const r = (this.rows || []).find((x) => x.user?.id === userId);
      if (!r) return `User #${userId}`;
      const u = r.user || {};
      const meta = u.phone || u.email || "";
      return meta ? `${u.name} (${meta})` : u.name;
    },

    // ----- Opening helpers (works with either has_opening_balance OR opening_balance object) -----
    hasOpening(r) {
      // prefer backend truth
      if (typeof r.has_opening === "boolean") return r.has_opening;
      if (typeof r.opening_balance_set === "boolean")
        return r.opening_balance_set;
      // fallback
      return r.opening_balance != null && Number(r.opening_balance) > 0;
    },
    openingAmount(r) {
      // opening_balance is a NUMBER in your response
      const v = r.opening_balance;
      return v == null ? 0 : Number(v);
    },
    openingAsOf(r) {
      return r.opening_as_of || "";
    },

    isRowBlocked(r) {
      // blocked if no opening OR no commitment OR explicitly blocked by backend
      if (r.can_process === false) return true;
      if (!this.hasOpening(r)) return true;
      if (r.commitment_amount == null) return true;
      // if already paid/late AND fully covered you might mark can_process false from backend; rely on backend for that
      return false;
    },

    blockReason(r) {
      if (r.can_process === false)
        return r.reason || "Blocked for this period.";
      if (!this.hasOpening(r)) return "Opening balance is required first.";
      if (r.commitment_amount == null) return "Commitment is required first.";
      return "Blocked.";
    },

    rowClass(r) {
      if (r.ui.error) return "table-danger";
      if (this.isRowBlocked(r)) return "table-secondary";
      if (r.existing) return "";
      return "";
    },

    validateRow(r) {
      r.ui.error = "";
      r.ui.warning = "";

      const amount = Number(r.input.amount || 0);

      if (amount < 0) {
        r.ui.error = "Amount cannot be negative.";
        return;
      }

      if (amount > 0) {
        if (this.isRowBlocked(r)) {
          r.ui.error = this.blockReason(r);
          return;
        }
        if (!this.defaults.paid_date) {
          r.ui.error = "Default Paid Date is required.";
          return;
        }
      }

      if (!this.hasOpening(r)) {
        r.ui.warning = "Opening balance missing.";
      } else if (r.commitment_amount == null) {
        r.ui.warning = "Commitment missing.";
      } else if (r.existing && amount > 0) {
        r.ui.warning =
          "Existing record will be topped up; extra may carry forward.";
      }
    },

    async loadMembers() {
      try {
        const res = await http.get("/members/dropdown");
        const raw = res.data?.data ?? res.data ?? [];

        this.members = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
      } catch (e) {
        this.members = [];
        this.error =
          e?.response?.data?.message ||
          "Failed to load members.";
      }
    },

    async loadFinancialYears() {
      try {
        const res = await http.get("/financial-year-rules");
        const raw = res.data?.data ?? res.data ?? [];
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];

        this.financialYears = [...list].sort(
          (a, b) =>
            Number(Boolean(b.is_active)) -
              Number(Boolean(a.is_active)) ||
            String(b.start_date || "").localeCompare(
              String(a.start_date || "")
            )
        );

        const active =
          this.financialYears.find((fy) => Boolean(fy.is_active)) ||
          this.financialYears[0] ||
          null;

        this.activeFinancialYearId = active?.id || null;

        const saved = localStorage.getItem(
          "admin_bulk_contributions_financial_year"
        );

        const savedExists =
          saved &&
          this.financialYears.some(
            (fy) => String(fy.id) === String(saved)
          );

        this.financialYearRuleId = savedExists
          ? Number(saved)
          : this.activeFinancialYearId || "";

        this.period = this.selectedYearDefaultPeriod;
        this.defaults.paid_date = this.selectedYearDefaultDate;
        this.defaults.expected_date = "";
      } catch (e) {
        this.financialYears = [];
        this.financialYearRuleId = "";
        this.activeFinancialYearId = null;

        this.error =
          e?.response?.data?.message ||
          "Failed to load financial years.";
      }
    },

    onFinancialYearChanged() {
      localStorage.setItem(
        "admin_bulk_contributions_financial_year",
        String(this.financialYearRuleId || "")
      );

      this.period = this.selectedYearDefaultPeriod;
      this.defaults.paid_date = this.selectedYearDefaultDate;
      this.defaults.expected_date = "";
      this.rows = [];
      this.result = null;

      this.preview();
    },

    goToActiveYear() {
      if (!this.activeFinancialYear) return;

      this.financialYearRuleId = this.activeFinancialYear.id;
      this.onFinancialYearChanged();
    },

    // ----- Modals wiring -----
    openCommitmentForRow(r) {
      if (!this.isViewingActiveYear) return;

      this.$refs.commitmentModal?.openForMember?.(
        r.user,
        this.period,
        this.financialYearRuleId
      );
    },

    openOpeningForRow(r) {
      if (!this.isViewingActiveYear) return;

      // support either openForMember(member) or open(member)
      const modal = this.$refs.openingModal;
      if (modal?.openForMember) {
        return modal.openForMember(
          r.user,
          this.financialYearRuleId
        );
      }

      if (modal?.open) {
        return modal.open(
          r.user,
          this.financialYearRuleId
        );
      }
      // fallback: still help debugging
      console.warn("OpeningBalanceModal missing open/openForMember method");
    },

    async onAnyModalSaved() {
      await this.preview();
    },

    fillCommitted() {
      if (!this.isViewingActiveYear) return;

      (this.rows || []).forEach((r) => {
        if (this.isRowBlocked(r)) return;
        if (Number(r.input.amount || 0) > 0) return;
        if (r.commitment_amount == null) return;
        r.input.amount = Number(r.commitment_amount);
        this.validateRow(r);
      });
    },

    clearInputs() {
      if (!this.isViewingActiveYear) return;

      (this.rows || []).forEach((r) => {
        r.input.amount = 0;
        r.ui.error = "";
        r.ui.warning = "";
      });
      this.result = null;
    },

    async preview() {
      if (!this.financialYearRuleId || !this.period) {
        this.rows = [];
        return;
      }

      this.loading = true;
      this.error = "";
      this.result = null;

      try {
        const res = await http.get("/contributions/bulk/preview", {
          params: {
            financial_year_rule_id: this.financialYearRuleId,
            period: this.period,
          },
        });

        const expectedDefault = res.data.expected_date_default || "";
        if (!this.defaults.expected_date)
          this.defaults.expected_date = expectedDefault;

        this.rows = (res.data.rows || []).map((r) => {
          const row = {
            ...r,
            input: { amount: 0 },
            ui: { error: "", warning: "" },
          };

          // initial warnings
          if (!this.hasOpening(row))
            row.ui.warning = "Opening balance missing.";
          else if (row.commitment_amount == null)
            row.ui.warning = "Commitment missing.";
          else if (row.can_process === false)
            row.ui.warning = row.reason || "Blocked for this period.";

          return row;
        });
      } catch (e) {
        const errors = e?.response?.data?.errors;
        const firstError = errors
          ? Object.values(errors)?.[0]?.[0]
          : null;

        this.error =
          firstError ||
          e?.response?.data?.message ||
          "Failed to load preview.";
      } finally {
        this.loading = false;
      }
    },

    openConfirm() {
      if (!this.isViewingActiveYear) return;

      (this.rows || []).forEach((r) => this.validateRow(r));

      if (this.blockingIssues.length > 0) return;

      this.confirmModal?.show();
    },

    async submit() {
      if (!this.isViewingActiveYear) {
        this.error =
          "Bulk contributions can only be processed in the active financial year.";
        return;
      }

      this.saving = true;
      this.error = "";
      this.result = null;

      try {
        const payload = {
          financial_year_rule_id: this.financialYearRuleId,
          period: this.period,
          paid_date: this.defaults.paid_date || null,
          expected_date: this.defaults.expected_date || null,
          items: this.submitItems,
        };

        const res = await http.post("/contributions/bulk", payload);

        this.result = res.data.data || null;
        this.confirmModal.hide();
        await this.preview();
      } catch (e) {
        const errors = e?.response?.data?.errors;
        const firstError = errors
          ? Object.values(errors)?.[0]?.[0]
          : null;

        this.error =
          firstError ||
          e?.response?.data?.message ||
          "Bulk processing failed.";
      } finally {
        this.saving = false;
      }
    },
  },

  async mounted() {
    this.confirmModal = Modal.getOrCreateInstance(
      document.getElementById("bulkConfirmModal")
    );

    await this.loadFinancialYears();

    if (!this.financialYearRuleId) return;

    await Promise.all([
      this.loadMembers(),
      this.preview(),
    ]);
  },
};
</script>

<style scoped>
.bulk-page .min-w-0 {
  min-width: 0;
}
.table td,
.table th {
  vertical-align: middle;
}
.topbar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: transparent;
}
</style>
